import { Link } from '@remix-run/react';
import cx from 'classnames';
import { useMemo } from 'react';

import getNormalizedCategory from '~/utils/getNormalizedCategory';

import { useWebThemeContext } from '../Layout/Context/useWebThemeContext';
import Typography from '../Typography';
import type { PostCardStyles } from './types';

interface Props {
  tags: {
    id: string;
    display: string;
  }[];
  cardStyles: PostCardStyles;
  className?: string;
}

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
const TagLink = ({ tag, bgColor, font, textColor, to, text, radiusClass }: any) => {
  return (
    <div className="relative">
      <Link
        to={to}
        style={{
          backgroundColor: bgColor,
          opacity: 0.8,
        }}
        className={cx(radiusClass, 'w-fit cursor-pointer px-4 py-2 text-xs text-white transition-all hover:bg-sky-950')}
      >
        <Typography
          as="span"
          size={'xs'}
          fontFamily={font}
          fontFamilyv2={font}
          className="whitespace-nowrap text-xs font-medium text-gray-50 opacity-0"
          color={textColor}
        >
          <span className="whitespace-nowrap pb-1 text-xs font-medium">{text}</span>
        </Typography>
      </Link>
      <Typography
        as="span"
        size={'xs'}
        fontFamily={font}
        fontFamilyv2={font}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform pt-[1px] text-xs font-medium text-gray-50 sm:pt-0"
        color={textColor}
      >
        <span className="whitespace-nowrap pb-1 text-xs font-medium">{text}</span>
      </Typography>
    </div>
  );
};

const PostTags = ({ tags, cardStyles, className }: Props) => {
  const { classes } = useWebThemeContext();

  const tagsLength = tags.length;
  const hasMoreThan1Tag = tagsLength > 1;
  const { font, tags_bg_color, tags_text_color } = cardStyles;
  const remainderLength = tagsLength - 1;

  const tagsToRender = useMemo(() => {
    return hasMoreThan1Tag ? tags.slice(0, 1) : tags;
  }, [hasMoreThan1Tag, tags]);

  return (
    <div className={cx('flex items-center gap-1', className)}>
      {tagsToRender.map((tag) => {
        return (
          <TagLink
            key={tag.id}
            to={`/t/${getNormalizedCategory(tag.display)}`}
            bgColor={tags_bg_color}
            font={font}
            textColor={tags_text_color}
            text={tag.display}
            radiusClass={classes.radius}
          />
        );
      })}

      {hasMoreThan1Tag && (
        <TagLink
          to={`/t/${getNormalizedCategory(tags[1].display)}`}
          bgColor={tags_bg_color}
          font={font}
          textColor={tags_text_color}
          text={`+${remainderLength}`}
          radiusClass={classes.radius}
        />
      )}
    </div>
  );
};

export default PostTags;
